import React from 'react';
import { Palette, Type } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';

const TEXT_COLORS = [
  '#FFFFFF', '#F3F4F6', '#E5E7EB', '#D1D5DB',
  '#9CA3AF', '#6B7280', '#374151', '#1F2937',
  '#EF4444', '#F97316', '#F59E0B', '#EAB308',
  '#84CC16', '#22C55E', '#10B981', '#06B6D4',
  '#3B82F6', '#6366F1', '#8B5CF6', '#EC4899'
];

export default function TextPropertiesPanel({ 
  element, 
  onUpdate,
  position 
}) {
  if (!element || element.type !== 'text') return null;
  
  const props = element.properties || {};
  const currentColor = props.fill || '#FFFFFF';
  const currentFontSize = props.fontSize || 16;

  const handleColorChange = (color) => {
    onUpdate(element.id, {
      properties: { ...element.properties, fill: color }
    });
  };

  const handleFontSizeChange = (size) => {
    onUpdate(element.id, {
      properties: { ...element.properties, fontSize: size }
    });
  };

  return (
    <div 
      className="absolute z-50 flex items-center gap-1 bg-background rounded-lg border shadow-lg p-1"
      style={{ 
        left: position?.x || 100, 
        top: Math.max(10, (position?.y || 100) - 50)
      }}
    >
      {/* Color Picker */}
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 px-2 gap-2"
            data-testid="text-color-picker"
          >
            <div 
              className="w-4 h-4 rounded-full border border-gray-300"
              style={{ backgroundColor: currentColor }}
            />
            <Palette className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" side="bottom">
          <p className="text-xs font-medium mb-2">Text Color</p>
          <div className="grid grid-cols-5 gap-1">
            {TEXT_COLORS.map(color => (
              <button
                key={color}
                className={`w-6 h-6 rounded-full border-2 transition-all ${
                  currentColor === color 
                    ? 'border-primary scale-110' 
                    : 'border-transparent hover:border-gray-400'
                }`}
                style={{ backgroundColor: color }}
                onClick={() => handleColorChange(color)}
                data-testid={`color-option-${color.replace('#', '')}`}
              />
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Font Size */}
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 px-2 gap-1"
            data-testid="text-size-picker"
          >
            <Type className="h-4 w-4" />
            <span className="text-xs">{currentFontSize}px</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-3" side="bottom">
          <p className="text-xs font-medium mb-2">Font Size: {currentFontSize}px</p>
          <Slider
            value={[currentFontSize]}
            onValueChange={([val]) => handleFontSizeChange(val)}
            min={12}
            max={72}
            step={2}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
